import { useTeam } from '@vrplatform/data';
import type { MRT_ColumnDef } from '@vrplatform/table';
import { useMantineTheme } from '@mantine/core';
import { formatCurrency, hasValue } from '@vrplatform/utils';
import { useMemo } from 'react';
import { useEmbedViewMode } from '../../providers';
import { getProfitAndLossTableCellStyle } from './_styles';
import {
  ProfitAndLossTableCell,
  type ProfitAndLossJournalEntriesArgs,
} from './ProfitAndLossTableCell';
import { useProfitAndLossTableFilter } from './useProfitAndLossTableFilter';
import type {
  ProfitAndLossRow,
  ProfitAndLossTableData,
} from './useProfitAndLossTableQuery';

type Column = MRT_ColumnDef<ProfitAndLossRow>;

const isColumnZero = (rows: ProfitAndLossRow[], columnId: string): boolean => {
  return rows.every(
    (row) =>
      (row.values[columnId]?.total ?? 0) === 0 &&
      isColumnZero(row.subRows ?? [], columnId)
  );
};

export const useProfitAndLossTableColumns = ({
  columns: initialColumns,
  onOpenJournalEntries,
  rows,
}: {
  columns: ProfitAndLossTableData['columns'];
  onOpenJournalEntries?: (args: ProfitAndLossJournalEntriesArgs) => void;
  rows: ProfitAndLossTableData['rows'];
}) => {
  const theme = useMantineTheme();
  const [{ currency }] = useTeam();
  const { isEmbed } = useEmbedViewMode();
  const { filter } = useProfitAndLossTableFilter();

  const hideZeroColumns = useMemo(() => {
    return filter.view?.includes('non-zero-columns');
  }, [filter.view]);

  return useMemo<Column[]>(() => {
    const base: Column[] = [
      {
        header: 'Title',
        Header: '',
        id: 'title',
        accessorKey: 'title',
        minSize: 300,
        maxSize: 0,
        size: 300,
        mantineTableBodyCellProps: (props) => ({
          sx: getProfitAndLossTableCellStyle(theme, {
            rowDepth: props.row.depth,
            type: props.row.original.type,
          }),
        }),
        Cell: ({ row, cell }) => {
          return (
            <ProfitAndLossTableCell.Base
              depth={row.depth}
              type={row.original.type}
              align="left"
              size={row.depth === 0 ? 'md' : 'sm'}
              breakWord
            >
              {cell.getValue<string | number>()}
            </ProfitAndLossTableCell.Base>
          );
        },
      },
      {
        header: 'Total',
        id: 'total',
        mantineTableHeadCellProps: {
          align: 'right',
          sx: (theme) => ({
            fontWeight: 400,
            color: theme.colors.gray[7],
            textAlign: 'right',
          }),
        },
        mantineTableBodyCellProps: (props) => ({
          align: 'right',
          sx: {
            textAlign: 'right',
            ...getProfitAndLossTableCellStyle(theme, {
              rowDepth: props.row.depth,
              type: props.row.original.type,
            }),
          },
        }),
        accessorFn: (row) => {
          if (row.total === undefined) return '';

          return formatCurrency((row.total ?? 0) / 100, currency);
        },
        Cell: ({ cell, row }) => {
          const totalPercentage = row.original.totalPercentage;
          const onClick =
            isEmbed || !onOpenJournalEntries
              ? undefined
              : row.original.type === 'account'
                ? () => {
                    onOpenJournalEntries({
                      accountId: row.original.id,
                    });
                  }
                : row.original.type === 'category'
                  ? () => {
                      onOpenJournalEntries({
                        categoryId: row.original.id,
                      });
                    }
                  : undefined;

          return (
            <ProfitAndLossTableCell.Base
              depth={row.depth}
              align="right"
              percentage={
                totalPercentage !== undefined ? totalPercentage : undefined
              }
              type={row.original.type}
              onClick={onClick}
            >
              {cell.getValue<string | number>()}
            </ProfitAndLossTableCell.Base>
          );
        },
      },
    ];

    if (!initialColumns) {
      return [];
    }

    let columns = [...initialColumns];
    if (columns.some((x) => x.title.toLowerCase() === 'unassigned listing')) {
      columns = columns.sort((a, b) => {
        if (a.title.toLowerCase() === 'unassigned listing') return -1;
        if (b.title.toLowerCase() === 'unassigned listing') return 1;
        return 0;
      });
    }

    return [
      base[0],
      ...columns
        .map<Column | undefined>((column) => {
          if (hideZeroColumns && isColumnZero(rows, column.id)) {
            return undefined;
          }

          return {
            header: column.title,
            id: column.id,
            mantineTableHeadCellProps: {
              align: 'right',
              sx: (theme) => ({
                fontWeight: 400,
                color: theme.colors.gray[7],
                textAlign: 'right',
              }),
            },
            mantineTableBodyCellProps: (props) => ({
              align: 'right',
              sx: {
                textAlign: 'right',
                ...getProfitAndLossTableCellStyle(theme, {
                  rowDepth: props.row.depth,
                  type: props.row.original.type,
                }),
              },
            }),
            accessorFn: (row) => {
              const value = row.values[column.id];

              if (value === undefined) return formatCurrency(0, currency);

              return formatCurrency(
                (row.values[column.id].total ?? 0) / 100,
                currency
              );
            },
            Cell: (props) => (
              <ProfitAndLossTableCell.Number
                row={props.row.original}
                depth={props.row.depth}
                columndId={column.id}
                onOpenJournalEntries={onOpenJournalEntries}
              >
                {props.cell.getValue<string | number>()}
              </ProfitAndLossTableCell.Number>
            ),
          };
        })
        .filter(hasValue),
      base[1],
    ];
  }, [
    currency,
    isEmbed,
    theme,
    hideZeroColumns,
    initialColumns,
    rows,
    onOpenJournalEntries,
  ]);
};
